import { CommonModule } from '@angular/common';
import { Component, OnInit, signal } from '@angular/core';
import {
  FormArray,
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { State, City } from 'country-state-city';
import {
  CompanyItem,
  CreateCompanyRequest,
  UpdateCompanyRequest,
  AdjustWalletRequest,
} from '../../data/models/company.models';
import {
  CompanyPricingItem,
  UpdateCompanyPricingRequest,
} from '../../data/models/company-pricing.models';
import { CompaniesService } from '../../data/services/companies.service';
import { GlobalAlertService } from '../../../../core/services/global-alert.service';

@Component({
  selector: 'app-companies',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule],
  templateUrl: './companies.html',
  styleUrl: './companies.scss',
})
export class Companies implements OnInit {
  searchTerm = '';
  private searchSubject = new Subject<string>();

  companies: CompanyItem[] = [];
  totalCount = 0;
  pageNumber = 1;
  pageSize = 20;
  totalPages = 0;

  isLoading = false;
  errorMessage = '';

  isModalOpen = false;
  isEditMode = false;
  isSaving = false;
  selectedCompanyId: number | null = null;
  deletingCompanyId: number | null = null;
  
  isDeleteModalOpen = signal(false);
  deletingCompany: CompanyItem | null = null;

  isPricingModalOpen = false;
  selectedPricingCompany: CompanyItem | null = null;
  isPricingLoading = false;
  pricingErrorMessage = '';
  isPricingSaving = false;

  companyForm: FormGroup;
  pricingForm: FormGroup;
  walletForm: FormGroup;

  isWalletModalOpen = false;
  walletAdjustmentType: 'Credit' | 'Debit' = 'Credit';
  walletAdjustingCompany: CompanyItem | null = null;
  isWalletSaving = false;
  walletErrorMessage = '';

  readonly statusOptions = ['Active', 'Inactive'];
  usStates = State.getStatesOfCountry('US');
  usCities: any[] = [];

  constructor(
    private companiesService: CompaniesService,
    private fb: FormBuilder,
    private globalAlertService: GlobalAlertService
  ) {
    this.companyForm = this.fb.group({
      name: ['', [Validators.required]],
      email: ['', [Validators.required, Validators.email]],
      phoneNumber: [''],
      addressLine1: [''],
      addressLine2: [''],
      city: [''],
      state: [''],
      zipCode: [''],
      country: [{ value: 'USA', disabled: true }],
      status: ['Active', [Validators.required]],
    });

    this.pricingForm = this.fb.group({
      items: this.fb.array([]),
    });

    this.walletForm = this.fb.group({
      amount: ['', [Validators.required, Validators.min(0.01)]],
      reason: ['', [Validators.required]],
    });

    this.companyForm.get('state')?.valueChanges.subscribe((stateCode) => {
      this.usCities = stateCode ? City.getCitiesOfState('US', stateCode) : [];
      const currentCity = this.companyForm.get('city')?.value;
      if (stateCode && !this.usCities.find((c) => c.name === currentCity)) {
        this.companyForm.get('city')?.setValue('');
      }
    });

    this.searchSubject
      .pipe(debounceTime(400), distinctUntilChanged())
      .subscribe((term) => {
        this.searchTerm = term;
        this.pageNumber = 1;
        this.loadCompanies();
      });
  }

  ngOnInit(): void {
    this.loadCompanies();
  }

  onSearchChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.searchSubject.next(input.value);
  }

  get pricingItemsArray(): FormArray {
    return this.pricingForm.get('items') as FormArray;
  }

  loadCompanies(): void {
    this.isLoading = true;
    this.errorMessage = '';

    this.companiesService
      .getCompanies(this.pageNumber, this.pageSize, this.searchTerm)
      .subscribe({
        next: (response) => {
          if (response.success && response.data) {
            this.companies = response.data.companies;
            this.totalCount = response.data.totalCount;
            this.totalPages =
              response.data.totalPages ||
              Math.ceil(this.totalCount / this.pageSize);
          } else {
            this.errorMessage = response.message || 'Failed to load companies.';
            this.companies = [];
          }
          this.isLoading = false;
        },
        error: () => {
          this.errorMessage = 'Failed to load companies.';
          this.companies = [];
          this.isLoading = false;
        },
      });
  }

  // --- Pagination Methods ---
  nextPage(): void {
    if (this.pageNumber < this.totalPages) {
      this.pageNumber++;
      this.loadCompanies();
    }
  }

  previousPage(): void {
    if (this.pageNumber > 1) {
      this.pageNumber--;
      this.loadCompanies();
    }
  }

  // --- Company Modal ---
  openCreateModal(): void {
    this.isEditMode = false;
    this.selectedCompanyId = null;
    this.companyForm.reset({
      name: '',
      email: '',
      phoneNumber: '',
      addressLine1: '',
      addressLine2: '',
      city: '',
      state: '',
      zipCode: '',
      country: 'USA',
      status: 'Active',
    });
    this.errorMessage = '';
    this.isModalOpen = true;
  }

  openEditModal(company: CompanyItem): void {
    this.isEditMode = true;
    this.selectedCompanyId = company.companyId;

    this.companyForm.patchValue({
      name: company.name,
      email: company.email,
      phoneNumber: company.phoneNumber,
      addressLine1: company.addressLine1,
      addressLine2: company.addressLine2,
      city: company.city,
      state: company.state,
      zipCode: company.zipCode,
      country: 'USA',
      status: company.isActive ? 'Active' : 'Inactive',
    });

    this.errorMessage = '';
    this.isModalOpen = true;
  }

  closeModal(): void {
    this.isModalOpen = false;
    this.companyForm.markAsPristine();
    this.companyForm.markAsUntouched();
  }

  saveCompany(): void {
    if (this.companyForm.invalid) {
      this.companyForm.markAllAsTouched();
      this.errorMessage = 'Please fill out all required fields correctly (Name, valid Email).';
      return;
    }

    this.isSaving = true;
    this.errorMessage = '';

    const formValue = this.companyForm.getRawValue();
    const isActive = formValue.status === 'Active';

    if (this.isEditMode && this.selectedCompanyId !== null) {
      const payload: UpdateCompanyRequest = {
        name: formValue.name ?? '',
        email: formValue.email ?? '',
        phoneNumber: formValue.phoneNumber ?? '',
        addressLine1: formValue.addressLine1 ?? '',
        addressLine2: formValue.addressLine2 ?? '',
        city: formValue.city ?? '',
        state: formValue.state ?? '',
        zipCode: formValue.zipCode ?? '',
        country: formValue.country ?? '',
        isActive: isActive,
      };

      this.companiesService
        .updateCompany(this.selectedCompanyId, payload)
        .subscribe({
          next: (response) => {
            if (response.success) {
              this.loadCompanies();
              this.closeModal();
            } else {
              this.errorMessage =
                response.message || 'Failed to update company.';
            }
            this.isSaving = false;
          },
          error: () => {
            this.errorMessage = 'Failed to update company.';
            this.isSaving = false;
          },
        });
    } else {
      const payload: CreateCompanyRequest = {
        name: formValue.name ?? '',
        email: formValue.email ?? '',
        phoneNumber: formValue.phoneNumber ?? '',
        addressLine1: formValue.addressLine1 ?? '',
        addressLine2: formValue.addressLine2 ?? '',
        city: formValue.city ?? '',
        state: formValue.state ?? '',
        zipCode: formValue.zipCode ?? '',
        country: formValue.country ?? '',
        isActive: isActive,
      };

      this.companiesService.createCompany(payload).subscribe({
        next: (response) => {
          if (response.success) {
            this.pageNumber = 1;
            this.loadCompanies();
            this.closeModal();
          } else {
            this.errorMessage =
              response.message || 'Failed to create company.';
          }
          this.isSaving = false;
        },
        error: () => {
          this.errorMessage = 'Failed to create company.';
          this.isSaving = false;
        },
      });
    }
  }

  // --- Deletion Actions ---
  openDeleteModal(company: CompanyItem): void {
    this.deletingCompany = company;
    this.isDeleteModalOpen.set(true);
  }

  closeDeleteModal(): void {
    this.isDeleteModalOpen.set(false);
    this.deletingCompany = null;
  }

  confirmDeleteCompany(): void {
    if (!this.deletingCompany) return;

    const companyId = this.deletingCompany.companyId;
    this.deletingCompanyId = companyId;

    this.companiesService.deleteCompany(companyId).subscribe({
      next: (response) => {
        if (response.success) {
          if (this.companies.length === 1 && this.pageNumber > 1) {
            this.pageNumber--;
          }
          this.loadCompanies();
          this.closeDeleteModal();
        } else {
          this.errorMessage = response.message || 'Failed to delete company.';
          this.closeDeleteModal();
        }
        this.deletingCompanyId = null;
      },
      error: () => {
        this.errorMessage = 'Failed to delete company.';
        this.deletingCompanyId = null;
        this.closeDeleteModal();
      },
    });
  }

  onDeleteCompany(company: CompanyItem): void {
    this.openDeleteModal(company);
  }

  // --- Pricing Actions (Maintained as mocks) ---
  openPricingModal(company: CompanyItem): void {
    this.selectedPricingCompany = company;
    this.isPricingModalOpen = true;
    this.pricingErrorMessage = '';
    this.loadCompanyPricing(company.companyId);
  }

  closePricingModal(): void {
    this.isPricingModalOpen = false;
    this.selectedPricingCompany = null;
    this.pricingErrorMessage = '';
    this.isPricingLoading = false;
    this.isPricingSaving = false;
    this.pricingForm.setControl('items', this.fb.array([]));
  }

  loadCompanyPricing(companyId: number): void {
    this.isPricingLoading = true;
    this.pricingErrorMessage = '';

    this.companiesService.getCompanyPricing(companyId).subscribe({
      next: (response) => {
        if (response.success && response.data) {
          const formRows = response.data.items.map((item) =>
            this.createPricingRow(item),
          );
          formRows.push(
            this.createPricingRow({
              serviceId: 0,
              serviceCode: 'ESTIMATED_COST_CHARGE',
              service: 'Estimated Cost Charge',
              description: 'Additional percentage on estimated costs',
              cost: response.data.estimatedCostChargePercent,
              salePrice: null,
              unit: 'percentage',
            })
          );
          this.pricingForm.setControl('items', this.fb.array(formRows));
        } else {
          this.pricingErrorMessage = response.message || 'Failed to load company pricing.';
        }
        this.isPricingLoading = false;
      },
      error: () => {
        this.pricingErrorMessage = 'Failed to load company pricing.';
        this.isPricingLoading = false;
      },
    });
  }

  createPricingRow(item: CompanyPricingItem): FormGroup {
    return this.fb.group({
      serviceId: [item.serviceId],
      serviceCode: [item.serviceCode],
      service: [item.service],
      description: [item.description],
      cost: [item.cost],
      salePrice: [item.salePrice],
      unit: [item.unit],
    });
  }

  onConfig(company: CompanyItem): void {
    this.openPricingModal(company);
  }

  savePricing(): void {
    if (!this.selectedPricingCompany) return;

    const payload: UpdateCompanyPricingRequest = {
      companyId: this.selectedPricingCompany.companyId,
      estimatedCostChargePercent: 0,
      items: [],
    };

    this.pricingItemsArray.controls.forEach((control) => {
      const serviceCode = control.get('serviceCode')?.value;

      if (serviceCode === 'ESTIMATED_COST_CHARGE') {
        payload.estimatedCostChargePercent = this.parseNullableNumber(control.get('cost')?.value) || 0;
      } else {
        payload.items.push({
          serviceId: control.get('serviceId')?.value || 0,
          cost: this.parseNullableNumber(control.get('cost')?.value),
          salePrice: this.parseNullableNumber(control.get('salePrice')?.value),
          description: control.get('description')?.value || '',
        });
      }
    });

    this.isPricingSaving = true;
    this.pricingErrorMessage = '';

    this.companiesService.updateCompanyPricing(this.selectedPricingCompany.companyId, payload).subscribe({
      next: (response) => {
        if (response.success) {
          this.isPricingSaving = false;
          this.closePricingModal();
        } else {
          this.pricingErrorMessage = response.message || 'Failed to update company pricing.';
          this.isPricingSaving = false;
        }
      },
      error: () => {
        this.pricingErrorMessage = 'Failed to update company pricing.';
        this.isPricingSaving = false;
      },
    });
  }

  parseNullableNumber(value: unknown): number | null {
    if (value === '' || value === null || value === undefined) return null;
    const parsed = Number(value);
    return Number.isNaN(parsed) ? null : parsed;
  }

  // --- Wallet Adjustment ---
  openWalletModal(company: CompanyItem, type: 'Credit' | 'Debit'): void {
    this.walletAdjustingCompany = company;
    this.walletAdjustmentType = type;
    this.walletErrorMessage = '';
    this.isWalletSaving = false;
    this.walletForm.reset({
      amount: '',
      reason: ''
    });
    this.isWalletModalOpen = true;
  }

  closeWalletModal(): void {
    this.isWalletModalOpen = false;
    this.walletAdjustingCompany = null;
    this.walletForm.reset();
  }

  submitWalletAdjustment(): void {
    if (this.walletForm.invalid) {
      this.walletForm.markAllAsTouched();
      return;
    }

    if (!this.walletAdjustingCompany) return;

    const amount = Number(this.walletForm.value.amount);
    const reason = this.walletForm.value.reason?.trim();

    if (this.walletAdjustmentType === 'Debit' && amount > (this.walletAdjustingCompany.availableCredits || 0)) {
      this.walletErrorMessage = 'Debit amount cannot exceed available credits.';
      return;
    }

    this.isWalletSaving = true;
    this.walletErrorMessage = '';

    const payload: AdjustWalletRequest = {
      amount,
      adjustmentType: this.walletAdjustmentType,
      reason
    };

    this.companiesService.adjustCompanyWallet(this.walletAdjustingCompany.companyId, payload).subscribe({
      next: (response: any) => {
        if (response.success) {
          this.globalAlertService.showSuccess('Wallet adjusted successfully.');
          this.loadCompanies();
          this.closeWalletModal();
        } else {
          this.errorMessage = response.message || 'Failed to adjust wallet.';
          this.globalAlertService.showError(this.errorMessage);
        }
        this.isWalletSaving = false;
      },
      error: () => {
        this.errorMessage = 'An error occurred while adjusting wallet.';
        this.globalAlertService.showError(this.errorMessage);
        this.isWalletSaving = false;
      }
    });
  }

  getSafeWalletBalance(value: number | undefined | null): number {
    if (value === undefined || value === null || isNaN(value)) return 0;
    if (value < 0) {
      console.warn(`[Companies] Negative value detected for wallet: ${value}. Displaying $0.00 instead.`);
      return 0;
    }
    return value;
  }

  // --- Utility ---
  clearError(): void {
    this.errorMessage = '';
  }

  get nameControl() {
    return this.companyForm.get('name');
  }

  get emailControl() {
    return this.companyForm.get('email');
  }
}
